import { Component, ContentChild, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { AsyncValidatorFn, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { Utils } from '../utils';
import { TitleTemplateDirective } from './directives/templates.directive';
import { FieldOptions } from './interfaces/field-options.interface';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit, OnChanges {
  @Input() public fields: (string | FieldOptions)[];
  @Input() public data: any[];
  @Input() public title: string;
  @Input() public noGroupText: string;
  @Input() public validators: ValidatorFn[] = [];
  @Input() public groupValidators: ValidatorFn[] = [];
  @Input() public asyncValidators: AsyncValidatorFn[] = [];
  @Input() public validatorsMessageTemplate: string | TemplateRef<any>;
  @Input() public showSeparator: boolean = true;
  @Input() public disabled: boolean = false;

  @ContentChild(TemplateRef, { static: true }) contentTemplateRef: TemplateRef<any>;
  @ContentChild(TitleTemplateDirective, { static: true, read: TemplateRef }) titleTemplate: TemplateRef<any>;

  @Output() public groupRemoved: EventEmitter<number> = new EventEmitter<number>();
  @Output() public groupAdded: EventEmitter<unknown> = new EventEmitter();

  public form: UntypedFormGroup;
  private readonly formControls: any = {};

  public get items(): UntypedFormArray {
    return this.form.controls.items as UntypedFormArray;
  }

  constructor(private readonly fb: UntypedFormBuilder) {
    this.form = this.fb.group({ items: this.fb.array([], this.validators, this.asyncValidators) });
  }

  public ngOnInit(): void {
    this.initControls();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.initControls();
  }

  public addNewGroup: () => void = (): void => {
    if (this.fields.length > 0) {
      const newControl: UntypedFormGroup | null = this.initNewFields();
      if (newControl) {
        this.items.push(newControl);
      }
    }
  };

  public removeGroup: (index: number) => void = (index: number): void => {
    this.items.removeAt(index);
  };

  public isTemplate(value: any): value is TemplateRef<any> {
    return value instanceof TemplateRef;
  }

  private initControls(): void {
    this.data = this.data || [];
    this.fields = this.fields || [];
    this.form = this.fb.group({ items: this.fb.array([], this.validators, this.asyncValidators) });

    if (this.data.length === 0 || this.fields.length === 0) {
      return;
    }
    this.data.forEach((item: any) => {
      const control: UntypedFormGroup | null = this.initNewFields();
      if (control) {
        const fieldValues: any = {};
        this.fields.forEach((field: string | FieldOptions) => {
          const key: string = Utils.isString(field) ? field : field.value;
          fieldValues[key] = item[key] ?? null; // '??' rather than '||' to keep '', '0' and 'false' as values
        });

        control.setValue(fieldValues);
        this.items.push(control);
      }
      this.disabled ? control.disable() : control.enable();
    });
  }

  private initNewFields(): UntypedFormGroup | null {
    this.fields.forEach((field: string | FieldOptions) => {
      if (Utils.isString(field)) {
        this.formControls[field] = [];
      } else {
        this.formControls[field.value] = [undefined, field.validators];
      }
    });
    return this.fb.group(this.formControls, { validators: this.groupValidators });
  }
}
